import React, { useState } from 'react'
import { mutate as mutateCache } from 'swr'
import { Alert, Button, Card, CardBody, Col, Row, Table } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faHistory, faPen } from '@fortawesome/free-solid-svg-icons'
import Flex from '../../../common/Flex'
import Loader from '../../../common/Loader'
import useSWRFetch from '../../../../helpers/hooks/useSWRFetch'
import { getRewardsConfig } from '../../../../helpers/api/rewardsAdmin'
import { formatDate, formatMultiplier, formatNumber, getLevelStyle } from '../../../rewards/rewardsUtils'
import { RULE_GROUPS, formatRuleValue, getRuleDefinition } from '../rewardsAdminUtils'
import ProgramSimulator, { DEFAULT_SIMULATION } from './ProgramSimulator'
import RuleEditModal from './RuleEditModal'
import RuleHistoryModal from './RuleHistoryModal'
import LevelEditModal from './LevelEditModal'
import ConfigAudits from './ConfigAudits'

const RuleRow = ({ rule, onEdit, onHistory }) => {
  const definition = getRuleDefinition(rule.key)
  return (
    <Row className="border-bottom border-200 py-2 align-items-center no-gutters">
      <Col xs={12} md={6} className="pr-md-3 mb-1 mb-md-0">
        <div className="fs--1 font-weight-semi-bold text-900">{definition.label}</div>
        <div className="fs--2 text-600">{definition.help}</div>
      </Col>
      <Col xs={7} md={3}>
        <div className={`fs--1 font-weight-semi-bold ${rule.value === null ? 'text-warning' : 'text-900'}`}>
          {formatRuleValue(rule.key, rule.value)}
        </div>
        {rule.valid_from && <div className="fs--2 text-600">Desde el {formatDate(rule.valid_from)}</div>}
      </Col>
      <Col xs={5} md={3} className="text-right">
        <Button color="falcon-default" size="sm" className="mr-1" onClick={() => onEdit(rule)} aria-label={`Editar ${definition.label}`}>
          <FontAwesomeIcon icon={faPen} />
          <span className="d-none d-lg-inline ml-1">Editar</span>
        </Button>
        <Button color="falcon-default" size="sm" onClick={() => onHistory(rule.key)} aria-label={`Historial de ${definition.label}`}>
          <FontAwesomeIcon icon={faHistory} />
          <span className="d-none d-lg-inline ml-1">Historial</span>
        </Button>
      </Col>
    </Row>
  )
}

const LevelsCard = ({ levels, onEdit }) => (
  <Card className="mb-3">
    <CardBody>
      <h5 className="fs-0 mb-1">Niveles</h5>
      <p className="fs--1 text-600 mb-2">
        Rango de XP de cada nivel y cuántas Coins más gana por pago. El cambio aplica desde el momento en que se guarda.
      </p>
      <Table size="sm" responsive className="fs--1 mb-0">
        <thead className="bg-200 text-900">
          <tr>
            <th>Nivel</th>
            <th className="text-right">XP desde</th>
            <th className="text-right">XP hasta</th>
            <th className="text-right">Multiplicador</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {levels.map((level) => {
            const style = getLevelStyle(level.badge_icon)
            return (
              <tr key={level.id}>
                <td>
                  <span className="d-inline-block rounded-circle mr-2 align-middle" style={{ width: 10, height: 10, background: style.color }} />
                  <span className="font-weight-semi-bold text-900">{level.name}</span>
                </td>
                <td className="text-right text-nowrap">{formatNumber(level.min_xp)} XP</td>
                <td className="text-right text-nowrap">{level.max_xp === null ? 'Sin tope' : `${formatNumber(level.max_xp)} XP`}</td>
                <td className="text-right">{formatMultiplier(level.coin_multiplier)}</td>
                <td className="text-right">
                  <Button color="falcon-default" size="sm" onClick={() => onEdit(level)} aria-label={`Editar nivel ${level.name}`}>
                    <FontAwesomeIcon icon={faPen} />
                  </Button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </Table>
    </CardBody>
  </Card>
)

// HO-TASK-289: parámetros económicos del programa (HO-TASK-276), con vigencia,
// historial, simulador y bitácora. La estructura de premios del sorteo no se
// configura acá todavía: depende de HO-TASK-273, que está en el backlog.
const RewardsConfig = () => {
  const { data, error, isLoading, mutate } = useSWRFetch('rewards-config', getRewardsConfig)
  const [simulation, setSimulation] = useState(DEFAULT_SIMULATION)
  const [editingRule, setEditingRule] = useState(null)
  const [historyKey, setHistoryKey] = useState(null)
  const [editingLevel, setEditingLevel] = useState(null)
  const [auditsVersion, setAuditsVersion] = useState(0)

  if (isLoading) {
    return (
      <Card className="mb-3">
        <Loader padding="py-5" />
      </Card>
    )
  }

  if (error || !data?.data) {
    return (
      <Alert color="danger">
        No pudimos cargar la configuración de Homelan Rewards.
        <Button color="link" size="sm" className="ml-1 p-0 align-baseline" onClick={() => mutate()}>
          Reintentar
        </Button>
      </Alert>
    )
  }

  const { rules = [], levels = [] } = data.data
  const byKey = rules.reduce((map, rule) => ({ ...map, [rule.key]: rule }), {})
  const missing = rules.filter((rule) => rule.value === null)

  // Un cambio guardado mueve la configuración, la simulación y la bitácora.
  // SWR 0.5 solo revalida por clave exacta, por eso la bitácora lleva una versión.
  const refresh = () => {
    mutate()
    mutateCache(['rewards-simulate', simulation.users, simulation.averageFeeUsd])
    setAuditsVersion((version) => version + 1)
  }

  return (
    <>
      {missing.length > 0 && (
        <Alert color="warning" className="fs--1">
          Hay {missing.length} {missing.length === 1 ? 'parámetro' : 'parámetros'} sin valor vigente. El programa usa un valor
          por defecto hasta que se configuren.
        </Alert>
      )}

      <ProgramSimulator simulation={simulation} onChange={setSimulation} />

      {RULE_GROUPS.map((group) => (
        <Card key={group.title} className="mb-3">
          <CardBody>
            <h5 className="fs-0 mb-1">{group.title}</h5>
            <p className="fs--1 text-600 mb-1">{group.description}</p>
            {group.keys
              .map((key) => byKey[key] || { key, value: null })
              .map((rule) => (
                <RuleRow key={rule.key} rule={rule} onEdit={setEditingRule} onHistory={setHistoryKey} />
              ))}
          </CardBody>
        </Card>
      ))}

      <LevelsCard levels={levels} onEdit={setEditingLevel} />

      <Flex className="mb-3">
        <Alert color="secondary" className="fs--1 mb-0 w-100">
          <b>Estructura de premios del sorteo:</b> se podrá configurar cuando exista el motor de sorteo (HO-TASK-273),
          que está en el backlog.
        </Alert>
      </Flex>

      <ConfigAudits version={auditsVersion} />

      <RuleEditModal
        rule={editingRule}
        rules={rules}
        simulation={simulation}
        onClose={() => setEditingRule(null)}
        onSaved={() => {
          setEditingRule(null)
          refresh()
        }}
      />
      <RuleHistoryModal ruleKey={historyKey} onClose={() => setHistoryKey(null)} />
      <LevelEditModal
        level={editingLevel}
        onClose={() => setEditingLevel(null)}
        onSaved={() => {
          setEditingLevel(null)
          refresh()
        }}
      />
    </>
  )
}

export default RewardsConfig
